import { z } from 'zod';

import { VoiceCallTransferCallToolDestinationSchema } from './transferTool.js';
import { type BaseItem } from '../../common.js';

export * from './transferTool.js';

export enum VoiceCallTool {
  authenticateInboundPatient = 'authenticateInboundPatient',
  authenticateKnownPatient = 'authenticateKnownPatient',
  bookSelectedSlot = 'bookSelectedSlot',
  getAppointments = 'getAppointments',
  /**
   * @deprecated - use getEncounters instead
   */
  getEncounterDetails = 'getEncounterDetails',
  getEncounters = 'getEncounters',
  getOrders = 'getOrders',
  getPatient = 'getPatient',
  lookupPatient = 'lookupPatient',
  searchForOpenSlots = 'searchForOpenSlots',
  sendInboundAuthCode = 'sendInboundAuthCode',
  sendSms = 'sendSms',
  traverseDecisionTree = 'traverseDecisionTree',
}

export enum VapiToolTypes {
  EndCall = 'EndCall',
  TransferCall = 'TransferCall',
  Voicemail = 'Voicemail',
}

export const toolTypesSchema = z.union([z.nativeEnum(VapiToolTypes), z.nativeEnum(VoiceCallTool)]);
export type ToolType = z.infer<typeof toolTypesSchema>;

export const UndiscriminatedBaseToolDefinitionSchema = z.object({
  type: toolTypesSchema,
  name: z.string(),
  organizationId: z.string().uuid(),
  description: z.string().nullable(),
  parameters: z.unknown(),
  startMessage: z.string().nullable(),
  failedMessage: z.string().nullable(),
  delayedMessage: z.string().nullable(),
  delayThresholdMilliseconds: z.number().nullable(),
  timeoutSeconds: z.number().nullable(),
  metadata: z.record(z.unknown()).nullable(),
});
const BaseNotableDefinedToolDefinitionSchema = UndiscriminatedBaseToolDefinitionSchema.extend({
  type: z.nativeEnum(VoiceCallTool),
});
export const NotableDefinedToolDefinitionUpsertValuesSchema =
  BaseNotableDefinedToolDefinitionSchema.pick({
    type: true,
    name: true,
    organizationId: true,
    startMessage: true,
    failedMessage: true,
    delayedMessage: true,
    delayThresholdMilliseconds: true,
    metadata: true,
  });

const BaseDecisionTreeToolDefinitionSchema = UndiscriminatedBaseToolDefinitionSchema.extend({
  type: z.literal(VoiceCallTool.traverseDecisionTree),
  metadata: z.object({
    decisionTreeId: z.string().uuid(),
  }),
});
export type BaseDecisionTreeToolDefinition = z.infer<typeof BaseDecisionTreeToolDefinitionSchema>;
export const DecisionTreeToolDefinitionUpsertValuesSchema =
  BaseDecisionTreeToolDefinitionSchema.pick({
    type: true,
    name: true,
    organizationId: true,
    metadata: true,
  });
export type DecisionTreeToolDefinitionUpsertValues = z.infer<
  typeof DecisionTreeToolDefinitionUpsertValuesSchema
>;

export type BaseNotableDefinedToolDefinition =
  | z.infer<typeof BaseNotableDefinedToolDefinitionSchema>
  | BaseDecisionTreeToolDefinition;
export type NotableDefinedToolDefinitionUpsertValues =
  | z.infer<typeof NotableDefinedToolDefinitionUpsertValuesSchema>
  | DecisionTreeToolDefinitionUpsertValues;

export const BaseTransferCallToolDefinitionSchema = UndiscriminatedBaseToolDefinitionSchema.extend({
  type: z.literal(VapiToolTypes.TransferCall),
  metadata: z.object({
    destinations: z.array(VoiceCallTransferCallToolDestinationSchema),
  }),
});
export const TransferCallToolDefinitionUpsertValuesSchema =
  BaseTransferCallToolDefinitionSchema.pick({
    type: true,
    name: true,
    organizationId: true,
    metadata: true,
  });
export type BaseTransferCallToolDefinition = z.infer<typeof BaseTransferCallToolDefinitionSchema>;
export type TransferCallToolDefinitionUpsertValues = z.infer<
  typeof TransferCallToolDefinitionUpsertValuesSchema
>;

export const BaseEndCallToolDefinitionSchema = UndiscriminatedBaseToolDefinitionSchema.extend({
  type: z.literal(VapiToolTypes.EndCall),
  startMessage: z.string().nullable(),
});
export const EndCallToolDefinitionUpsertValuesSchema = BaseEndCallToolDefinitionSchema.pick({
  type: true,
  name: true,
  organizationId: true,
  startMessage: true,
});
export type BaseEndCallToolDefinition = z.infer<typeof BaseEndCallToolDefinitionSchema>;
export type EndCallToolDefinitionUpsertValues = z.infer<
  typeof EndCallToolDefinitionUpsertValuesSchema
>;

export const ToolDefinitionUpsertValuesSchema = z.discriminatedUnion('type', [
  NotableDefinedToolDefinitionUpsertValuesSchema,
  TransferCallToolDefinitionUpsertValuesSchema,
  EndCallToolDefinitionUpsertValuesSchema,
]);
export type ToolDefinitionUpsertValues = z.infer<typeof ToolDefinitionUpsertValuesSchema>;

export const BaseToolDefinitionSchema = z.discriminatedUnion('type', [
  BaseTransferCallToolDefinitionSchema,
  BaseEndCallToolDefinitionSchema,
  BaseNotableDefinedToolDefinitionSchema,
]);
export type BaseToolDefinition = z.infer<typeof BaseToolDefinitionSchema>;
export type ToolDefinition = BaseItem & BaseToolDefinition;

export const ToolDefinitionFindAllOptionsSchema = z.object({
  organizationIds: z.array(z.string().uuid()).optional(),
  assistantDefinitionId: z.string().uuid().optional(),
  type: toolTypesSchema.optional(),
  name: z.string().optional(),
  description: z.string().optional(),
});
export type ToolDefinitionFindAllOptions = z.infer<typeof ToolDefinitionFindAllOptionsSchema>;

export function isNotableDefinedToolDefinition(toolDefinition: {
  type: VoiceCallTool | VapiToolTypes;
}): toolDefinition is BaseNotableDefinedToolDefinition {
  return Object.values(VoiceCallTool).some((type) => type === toolDefinition.type);
}

export function isDecisionTreeToolDefinition(toolDefinition: {
  type: VoiceCallTool | VapiToolTypes;
}): toolDefinition is BaseDecisionTreeToolDefinition {
  return toolDefinition.type === VoiceCallTool.traverseDecisionTree;
}

export function isTransferCallToolDefinition(toolDefinition: {
  type: VoiceCallTool | VapiToolTypes;
}): toolDefinition is BaseTransferCallToolDefinition {
  return toolDefinition.type === VapiToolTypes.TransferCall;
}

export function isEndCallToolDefinition(toolDefinition: {
  type: VoiceCallTool | VapiToolTypes;
}): toolDefinition is BaseEndCallToolDefinition {
  return toolDefinition.type === VapiToolTypes.EndCall;
}
